<template>
  <div>
    <Section>
      <SectionHeader :title="$t('apps.settings.message.general')"/>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="`${$t('apps.settings.message.username')} *`" />
        <BaseInput
          v-if="isEditPage"
          v-model="$v.input.username.$model"
          :disabled="true"
        />
        <BaseInput
          v-else
          v-model="$v.input.username.$model"
          :classNames="['text-lowercase', { 'is-invalid': $v.input.username.$error, 'is-valid': $v.input.username.isUnique }]"
          :invalidText="username.invalidText"
          :validText="username.validText"
          :disabled="disabled"
          :placeholder="$t('apps.settings.placeholder.username')"
          autocomplete="off"
        />
        </Col>
        <Col className="col-lg-6">
        <Label :text="`${$t('apps.settings.message.password')} *`" />
        <BaseInput
          v-if="isEditPage"
          v-model="$v.input.password.$model"
          :disabled="true"
        />
        <BaseInput
          v-else
          v-model="$v.input.password.$model"
          :classNames="[{ 'is-invalid': $v.input.password.$error, 'is-valid': !$v.input.password.$error && $v.input.password.$model }]"
          :disabled="disabled"
          :placeholder="$t('apps.settings.placeholder.password')"
          type="password"
          autocomplete="new-password"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.settings.message.setpermissions')"/>
      <FormRow>
        <Col className="col-lg-6">
        <BaseRadioRich
          v-model="input.type"
          :val="USER_PERMISSION_TYPE.GENERAL"
          :label="$t('apps.settings.message.radiotypegeneral')"
          :message="$t('apps.settings.message.radiotypegeneraldetail')"
          :disabled="false"
        />
        </Col>
        <Col className="col-lg-6">
        <BaseRadioRich
          v-model="input.type"
          :val="USER_PERMISSION_TYPE.RELATIONAL"
          :label="$t('apps.settings.message.radiotyperelational')"
          :message="$t('apps.settings.message.radiotyperelationaldetail')"
          :disabled="false"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-12">
        <SectionTitle :title="$t('apps.settings.message.setpermission')" />
        </Col>
        <Col
          v-if="input.type === USER_PERMISSION_TYPE.GENERAL"
          className="col-lg-12"
          style="margin-top: -20px;"
        >
        <BaseRolePermission
          :masterData="appRolePermissions"
          :data.sync="input.permissions"
        />
        </Col>
        <Col
          v-else-if="input.type === USER_PERMISSION_TYPE.RELATIONAL"
          className="col-lg-12"
          style="margin-top: -15px;"
        >
        <BaseNotFoundData
          v-if="!rolePermissionList.length"
          :title="$t('apps.settings.message.notfoundrolepermission')"
          :detail="$t('apps.settings.message.createnewrolepermission')"
        />
        <BaseSelectRolePermission
          v-else
          :rolePermissionList="rolePermissionList"
          :selected.sync="input.permissionIds"
        />
        </Col>
      </FormRow>
    </Section>

    <BaseFormAction
      :textCancel="$t('button.cancel')"
      :textSubmit="$t('button.save')"
      :isSubmitted="isSubmitted"
      @onCancel="handleCancel"
      @onSubmit="handleSubmit"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { required, minLength } from 'vuelidate/lib/validators'
import { cloneDeep } from 'lodash'
import { USER_PERMISSION_TYPE } from '@/helpers/enum'

export default {
  props: {
    state: {
      type: Object,
      default: null,
    },
    isSubmitted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    appRolePermissions: {
      type: Array,
      default: () => ([]),
    },
    rolePermissionList: {
      type: Array,
      default: () => ([]),
    },
  },

  data () {
    return {
      USER_PERMISSION_TYPE,
      input: {
        username: null,
        password: null,
        type: USER_PERMISSION_TYPE.GENERAL,
        permissions: [],
        permissionIds: [],
      },
      inputOriginal: null,
      debounce: null,
    }
  },

  validations: {
    input: {
      username: {
        required,
        isUnique (value) {
          if (!value) return false
          if (this.isEditPage) return true

          clearTimeout(this.debounce)
          return new Promise((resolve, reject) => {
            this.debounce = setTimeout(() => {
              const isUnique = this.isUniqueUsername(value)
              resolve(isUnique)
            }, 500)
          })
        },
      },
      password: {
        required,
        minLength: minLength(6),
      },
    },
  },

  computed: {
    username () {
      let invalidText = null
      let validText = null
      if (!this.$v.input.username.$isUnique && this.$v.input.username.$model) {
        invalidText = this.$t('message.notavailablecandidate', { candidate: this.$v.input.username.$model })
      }
      if (this.$v.input.username.isUnique) {
        validText = this.$t('message.availablecandidate', { candidate: this.$v.input.username.$model })
      }
      return {
        invalidText,
        validText,
      }
    },
    password () {
      let invalidText = null
      return {
        invalidText,
      }
    },
    isEditPage () {
      return !!this.state
    },
  },

  watch: {
    input: {
      handler: function () {
        this.$emit('update:form', this.input)
      },
      deep: true,
    },
  },

  created () {
    if (this.state) {
      this.setInput(this.state)
    } else {
      this.inputOriginal = cloneDeep(this.input)
    }
  },

  methods: {
    ...mapActions({
      checkCandidate: 'checkCandidate',
    }),
    handleCancel () {
      if (this.$listeners.onCancel) {
        this.$emit('onCancel')
      } else {
        this.input = cloneDeep(this.inputOriginal)
        this.scrollToTop(600)
      }
    },
    handleSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.scrollToTop(600)
      } else {
        this.$emit('onSubmit', this.input)
      }
    },
    setInput (state) {
      this.input.username = state.username
      this.input.password = state.unbcrypt_password
      this.input.type = state.permission_type
      if (state.permission_type === USER_PERMISSION_TYPE.GENERAL) {
        this.input.permissions = cloneDeep(state.permissions)
      } else if (state.permission_type === USER_PERMISSION_TYPE.RELATIONAL) {
        this.input.permissionIds = cloneDeep(state.permission_ids)
      }
      this.inputOriginal = cloneDeep(this.input)
    },
    async isUniqueUsername (username = '') {
      const { data } = await this.checkCandidate({ candidate: username })
      this.showFinish(this.$Progress)
      const isUnique = data.is_available
      return isUnique
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/combine/UserPermissionForm.vue